// Dependencies
import { createAction, createSlice } from '@reduxjs/toolkit';
import { PayloadAction } from '@reduxjs/toolkit/dist/createAction';
// Types
import { List } from '../objects/list';
import { Error } from '../objects/error';
import { Column } from '../objects/column';
import { CustomCurrency } from '../objects/customCurrency';
import { Currency } from '../../currency/objects/currency';
import { Preferences } from '../../profile/objects/preferences';
import { CustomCurrencyState } from '../objects/customCurrencyState';

const setPreferences = createAction<Preferences[]>('PROFILE_SET_PREFERENCES');

const initialState: CustomCurrencyState = {
    cancel_modal: false,
    columns: [{
        label: 'name',
        field_name: 'NAME',
        checked: true,
        order_by: 'name',
        fixed_width: false,
        last_updated: false
    }, {
        label: 'custom-currency-start-date',
        field_name: 'START_DATE',
        checked: true,
        order_by: 'start_date',
        fixed_width: true,
        last_updated: false
    }, {
        label: 'custom-currency-disable-date',
        field_name: 'DISABLE_DATE',
        checked: true,
        order_by: 'disable_date',
        fixed_width: true,
        last_updated: false
    }, {
        label: 'custom-currency-disabled',
        field_name: 'DISABLED',
        checked: true,
        order_by: 'custom-currency-disabled',
        fixed_width: true,
        last_updated: false
    }, {
        label: 'exchange-rate',
        field_name: 'EXCHANGE_RAGE',
        checked: true,
        order_by: '',
        fixed_width: false,
        last_updated: false
    }, {
        label: 'modified-date',
        field_name: 'MODIFIED_DATE',
        checked: true,
        order_by: 'modified_date',
        fixed_width: true,
        last_updated: false
    }, {
        label: 'modified-by',
        field_name: 'MODIFIED_BY',
        checked: true,
        order_by: 'last_author__username',
        fixed_width: true,
        last_updated: false
    }, {
        label: 'created-date',
        field_name: 'CREATED_DATE',
        checked: true,
        order_by: 'created_date',
        fixed_width: true,
        last_updated: false
    }, {
        label: 'created-by',
        field_name: 'CREATED_BY',
        checked: true,
        order_by: 'creator__username',
        fixed_width: true,
        last_updated: false
    }],
    count: 0,
    create: false,
    currency_from: null,
    currency_from_error: '',
    currency_to: null,
    currency_to_error: '',
    custom_currency: null,
    default_currencies: [],
    disable_date: null,
    disable_date_error: '',
    disable_modal: false,
    edit: false,
    export_full: false,
    export_list: false,
    export_type: 'CSV',
    factor: '',
    factor_error: '',
    filter_created_date_gte: null,
    filter_created_date_lte: null,
    filter_creator: '',
    filter_disabled: false,
    filter_last_author: '',
    filter_modified_date_gte: null,
    filter_modified_date_lte: null,
    filter_open: false,
    filter_period: 'CREATION',
    filter_period_type: 0,
    limit: 50,
    list: null,
    name: '',
    name_error: '',
    order: 'asc',
    order_by: 'name',
    page: 0,
    preferences: [],
    real_factor: '',
    real_factor_error: '',
    refresh: false,
    running: false,
    search: '',
    start_date: null,
    start_date_error: '',
    currencies: [],
};

export const customCurrencySlice = createSlice({
    name: 'customCurrency',
    initialState,
    reducers: {
        addPreferences: (state, action: PayloadAction<Preferences[]>) => {
            state.preferences = [...state.preferences, ...action.payload];
        },
        moveDownColumn: (state, action: PayloadAction<number>) => {
            const columns = [...state.columns];
            for (let i = 0; i < columns.length; i++) {
                columns[i].last_updated = false;
            }
            const column = {...columns[action.payload + 1]};
            columns[action.payload + 1] = columns[action.payload];
            columns[action.payload + 1].last_updated = true;
            columns[action.payload] = column;
            state.columns = columns;
        },
        moveUpColumn: (state, action: PayloadAction<number>) => {
            const columns = [...state.columns];
            for (let i = 0; i < columns.length; i++) {
                columns[i].last_updated = false;
            }
            const column = {...columns[action.payload - 1]};
            columns[action.payload - 1] = columns[action.payload];
            columns[action.payload - 1].last_updated = true;
            columns[action.payload] = column;
            state.columns = columns;
        },
        resetCustomCurrency: (state) => {
            state.cancel_modal = false;
            state.create = false;
            state.currency_from = null;
            state.currency_from_error = '';
            state.currency_to = null;
            state.currency_to_error = '';
            state.custom_currency = null;
            state.disable_date = null;
            state.disable_date_error = '';
            state.disable_modal = false;
            state.edit = false;
            state.factor = '';
            state.factor_error = '';
            state.name = '';
            state.name_error = '';
            state.real_factor = '';
            state.real_factor_error = '';
            state.start_date = null;
            state.start_date_error = '';
        },
        resetFilters: (state) => {
            state.filter_created_date_gte = null;
            state.filter_created_date_lte = null;
            state.filter_creator = '';
            state.filter_disabled = false;
            state.filter_disable_date_gte = null;
            state.filter_disable_date_lte = null;
            state.filter_last_author = '';
            state.filter_modified_date_gte = null;
            state.filter_modified_date_lte = null;
            state.filter_period = 'CREATION';
            state.filter_period_type = 0;
            state.filter_start_date_gte = null;
            state.filter_start_date_lte = null;
        },
        setCustomCurrency: (state, action: PayloadAction<CustomCurrency>) => {
            state.custom_currency = action.payload;
            state.edit = false;
        },
        setCustomCurrencyPreferences: (state, action: PayloadAction<Preferences[]>) => {
            state.preferences = action.payload;
        },
        setErrors: (state, action: PayloadAction<Error[]>) => {
            const errors = action.payload;
            errors.map((error: Error) => {
                state[error.key] = error.error;
            });
        },
        setExportType: (state, action: PayloadAction<'CSV' | 'PDF'>) => {
            state.export_type = action.payload;
        },
        setLimit: (state, action: PayloadAction<number>) => {
            state.list = null;
            state.limit = action.payload;
        },
        setList: (state, action: PayloadAction<List>) => {
            state.count = action.payload.count;
            state.list = action.payload.results;
            state.running = false;
        },
        setOrderBy: (state, action: PayloadAction<string>) => {
            state.list = null;
            state.order_by = action.payload;
            state.order = state.order_by === action.payload && state.order === 'asc' ? 'desc': 'asc';
        },
        setPage: (state, action: PayloadAction<number>) => {
            state.list = null;
            state.page = action.payload;
        },
        setRunning: (state, action: PayloadAction<boolean>) => {
            state.running = action.payload;
        },
        setSearch: (state, action: PayloadAction<string>) => {
            state.list = null;
            state.page = 0;
            state.search = action.payload;
        },
        toggleCancelModal: (state) => {
            state.cancel_modal = !state.cancel_modal;
        },
        toggleColumn: (state, action: PayloadAction<number>) => {
            const columns = [...state.columns];
            const column = {...columns[action.payload]};
            column.checked = !column.checked;
            columns[action.payload] = column;
            state.columns = columns;
        },
        toggleCreate: (state) => {
            state.create = !state.create;
            state.start_date = new Date();
        },
        toggleDisableModal: (state) => {
            state.disable_modal = !state.disable_modal;
        },
        toggleEdit: (state) => {
            state.edit = !state.edit;
            state.cancel_modal = false;
            if (state.custom_currency !== null) {
                state.disable_date = state.custom_currency.disable_date;
                state.disable_date_error = '';
                state.factor = state.custom_currency.factor;
                state.factor_error = '';
                state.name = state.custom_currency.name;
                state.name_error = '';
                state.real_factor = state.custom_currency.real_factor;
                state.real_factor_error = '';
            }
        },
        toggleExport: (state, action: PayloadAction<boolean>) => {
            state.export_list = action.payload;
        },
        toggleExportFull: (state) => {
            state.export_full = !state.export_full;
        },
        toggleFilters: (state) => {
            state.filter_open = !state.filter_open;
        },
        toggleRefresh: (state) => {
            state.refresh = !state.refresh;
        },
        updateCurrencyFrom: (state, action: PayloadAction<null | Currency>) => {
            state.currency_from = action.payload;
            state.currency_from_error = '';
        },
        updateCurrencyTo: (state, action: PayloadAction<null | Currency>) => {
            state.currency_to = action.payload;
            state.currency_to_error = '';
        },
        updateDisableDate: (state, action: PayloadAction<Date | null>) => {
            state.disable_date = action.payload;
            state.disable_date_error = '';
        },
        updateFactor: (state, action: PayloadAction<string | number>) => {
            state.factor = action.payload;
            state.factor_error = '';
        },
        updateFilterCreatedDateGte: (state, action: PayloadAction<Date | null>) => {
            state.filter_created_date_gte = action.payload;
        },
        updateFilterCreatedDateLte: (state, action: PayloadAction<Date | null>) => {
            state.filter_created_date_lte = action.payload;
        },
        updateFilterCreator: (state, action: PayloadAction<string>) => {
            state.filter_creator = action.payload;
        },
        updateFilterDisabled: (state) => {
            state.filter_disabled = !state.filter_disabled;
        },
        updateFilterLastAuthor: (state, action: PayloadAction<string>) => {
            state.filter_last_author = action.payload;
        },
        updateFilterModifiedDateGte: (state, action: PayloadAction<Date | null>) => {
            state.filter_modified_date_gte = action.payload;
        },
        updateFilterModifiedDateLte: (state, action: PayloadAction<Date | null>) => {
            state.filter_modified_date_lte = action.payload;
        },
        updateFilterPeriod: (state, action: PayloadAction<'CREATION' | 'MODIFICATION'>) => {
            state.filter_period = action.payload;
            state.filter_period_type = 0;
            state.filter_created_date_gte = action.payload === 'MODIFICATION' ? null : state.filter_created_date_gte;
            state.filter_created_date_lte = action.payload === 'MODIFICATION' ? null : state.filter_created_date_lte;
            state.filter_modified_date_gte = action.payload === 'CREATION' ? null : state.filter_modified_date_gte;
            state.filter_modified_date_lte = action.payload === 'CREATION' ? null : state.filter_modified_date_lte;
        },
        updateFilterPeriodType: (state, action: PayloadAction<number>) => {
            state.filter_period_type = action.payload;
        },
        updateListItemDisabled: (state, action: PayloadAction<CustomCurrency>) => {
            if (state.list !== null) {
                const list = [...state.list];
                for (let i = 0; i < list.length; i++) {
                    if (list[i].id === action.payload.id) {
                        list[i] = action.payload;
                        break;
                    }
                }
                state.list = list;
            }
        },
        updateName: (state, action: PayloadAction<string>) => {
            state.name = action.payload;
            state.name_error = '';
        },
        updateRealFactor: (state, action: PayloadAction<string | number>) => {
            state.real_factor = action.payload;
            state.real_factor_error = '';
        },
        updateStartDate: (state, action: PayloadAction<Date | null>) => {
            state.start_date = action.payload;
            state.start_date_error = '';
        },
        setColumns: (state, action: PayloadAction<Column[]>) => {
            state.columns = action.payload;
        },
        setCurrencies: (state, action: PayloadAction<Currency[]>) => {
            state.currencies = action.payload;
        },
        setDefaultCurrencies: (state, action: PayloadAction<Currency[]>) => {
            state.default_currencies = action.payload;
        }
    },
    extraReducers: (builder) => {
        builder
            .addCase(setPreferences, (state, action) => {
                const field_name: string[] = [];
                const columns: Column[] = [];
                const preferences: Preferences[] = [];
                let order_by = '-created_date';
                let order: 'asc' | 'desc' = 'asc';
                action.payload.map((preference: Preferences) => {
                    if (preference.module === 'CUSTOM_CURRENCY') {
                        if (!field_name.includes(preference.field_name)) {
                            switch (preference.field_name) {
                                case 'NAME':
                                    columns.push({label: 'name', field_name: preference.field_name, checked: preference.active, order_by: 'name', fixed_width: false, last_updated: false});
                                    order_by = preference.ordering_asc !== null ? 'name' : order_by;
                                    break;
                                case 'START_DATE':
                                    columns.push({label: 'custom-currency-start-date', field_name: preference.field_name, checked: preference.active, order_by: 'start_date', fixed_width: true, last_updated: false});
                                    order_by = preference.ordering_asc !== null ? 'start_date' : order_by;
                                    break;
                                case 'DISABLE_DATE':
                                    columns.push({label: 'custom-currency-disable-date', field_name: preference.field_name, checked: preference.active, order_by: 'disable_date', fixed_width: true, last_updated: false});
                                    order_by = preference.ordering_asc !== null ? 'disable_date' : order_by;
                                    break;
                                case 'DISABLED':
                                    columns.push({label: 'custom-currency-disabled', field_name: preference.field_name, checked: preference.active, order_by: '', fixed_width: false, last_updated: false});
                                    order_by = preference.ordering_asc !== null ? '' : order_by;
                                    break;
                                case 'EXCHANGE_RAGE':
                                    columns.push({label: 'exchange-rate', field_name: preference.field_name, checked: preference.active, order_by: '', fixed_width: false, last_updated: false});
                                    order_by = preference.ordering_asc !== null ? '' : order_by;
                                    break;
                                case 'CREATED_DATE':
                                    columns.push({label: 'created-date', field_name: preference.field_name, checked: preference.active, order_by: 'created_date', fixed_width: true, last_updated: false});
                                    order_by = preference.ordering_asc !== null ? 'created_date' : order_by;
                                    break;
                                case 'CREATED_BY':
                                    columns.push({label: 'created-by', field_name: preference.field_name, checked: preference.active, order_by: 'creator__username', fixed_width: false, last_updated: false});
                                    order_by = preference.ordering_asc !== null ? 'creator__username' : order_by;
                                    break;
                                case 'MODIFIED_DATE':
                                    columns.push({label: 'modified-date', field_name: preference.field_name, checked: preference.active, order_by: 'modified_date', fixed_width: true, last_updated: false});
                                    order_by = preference.ordering_asc !== null ? 'modified_date' : order_by;
                                    break;
                                case 'MODIFIED_BY':
                                    columns.push({label: 'modified-by', field_name: preference.field_name, checked: preference.active, order_by: 'last_author__username', fixed_width: false, last_updated: false});
                                    order_by = preference.ordering_asc !== null ? 'last_author__username' : order_by;
                                    break;
                                default:
                                    break;
                            }
                            preferences.push(preference);
                            field_name.push(preference.field_name);
                            order = preference.ordering_asc !== null ? preference.ordering_asc ? 'asc' : 'desc' : order;
                        }
                    }
                });
                if (columns.length !== state.columns.length) {
                    state.columns.map(column => {
                        let added = false;
                        for (let i = 0; i < columns.length; i++) {
                            if (columns[i].label === column.label) {
                                added = true;
                                break;
                            }
                        }
                        if (!added) {
                            columns.push({...column});
                        }
                    });
                }
                state.preferences = preferences;
                state.columns = columns;
                state.order_by = order_by;
                state.order = order;
            })
            .addDefaultCase(() => {})
    }
});

export const {
    setDefaultCurrencies,
    addPreferences,
    moveDownColumn,
    moveUpColumn,
    resetCustomCurrency,
    resetFilters,
    setCustomCurrency,
    setErrors,
    setExportType,
    setLimit,
    setList,
    setOrderBy,
    setPage,
    setCustomCurrencyPreferences,
    setRunning,
    setSearch,
    toggleCancelModal,
    toggleColumn,
    toggleCreate,
    toggleDisableModal,
    toggleEdit,
    toggleExport,
    toggleExportFull,
    toggleFilters,
    toggleRefresh,
    updateFilterCreatedDateGte,
    updateFilterCreatedDateLte,
    updateFilterCreator,
    updateFilterDisabled,
    updateFilterLastAuthor,
    updateFilterModifiedDateGte,
    updateFilterModifiedDateLte,
    updateFilterPeriod,
    updateFilterPeriodType,
    updateListItemDisabled,
    updateCurrencyFrom,
    updateCurrencyTo,
    updateDisableDate,
    updateFactor,
    updateName,
    updateRealFactor,
    updateStartDate,
    setColumns,
    setCurrencies
} = customCurrencySlice.actions;

export default customCurrencySlice.reducer;